import { useMemo, useCallback, Fragment } from "react"
import {
  Box,
  Divider,
  List,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useDirectory } from "hooks"

import { PageLayout } from "layouts/PageLayout"

import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { LevelFilterBar, ShopListItem } from "components/domains/directory"
import { Head } from "components/Head"
import { StoreIcon } from "components/icons"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { FeatureSkeletonListItem } from "components/domains/features"
import { IPageLayoutProps } from "layouts"

interface IDirectoryProps {
  Layout?: React.FC<IPageLayoutProps>
  AppNavigation?: React.ComponentType
}

export const Directory = ({
  Layout = PageLayout,
  AppNavigation,
}: IDirectoryProps) => {
  const { t } = useTranslation()

  const {
    handleChangeParentCategory,
    handleChangeSubCategory,
    handleChangeOrdinal,
    handleSelectLevelChange,
    changeVenueFilter,
    resetFilter,
    filteredOccupantsSorted,
    filteredLevels,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    ordinalFilter,
    venueFilter,
    levelOptions,
    venueOptions,
    defaultVenueFilterValue,
    ref,
    endScrollRef,
    hasNextPage,
    showSubCategoryFilter,
    venues,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    locationVenueId,
    hasData,
  } = useDirectory()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    root,
    filterSection,
    scrollPane,
    categoryFilterBar,
    subCategoryFilterBar,
    levelFilterBar,
    list,
    divider,
    buildingFilterSelect,
    levelFilterSelect,
    buildingWithLevelFilterSection,
    selectMenuItem,
    resetButton,
    emptyDataWrapper,
    venueSelector,
    pageLayout,
  } = styleOverrides

  const disableFloorSelect = venueFilter === defaultVenueFilterValue

  const handleChangeVenue = useCallback(
    (e) => {
      changeVenueFilter(e?.target?.value)
    },
    [changeVenueFilter]
  )
  return (
    <>
      <Head title={t("Directory")} />
      <Layout
        className="directory-page-layout"
        sx={pageLayout}
        AppNavigation={AppNavigation}
      >
        <Box ref={ref} sx={root}>
          <Box sx={filterSection}>
            <HorizontalScrollPane
              className="category-filterbar"
              stackProps={{
                sx: scrollPane,
              }}
            >
              <CategoryFilterBar
                sx={categoryFilterBar}
                activeCategory={parentCategoryId}
                categories={sortedParentCategoryWithOccupant}
                onChange={handleChangeParentCategory}
              />
            </HorizontalScrollPane>
            {showSubCategoryFilter && (
              <HorizontalScrollPane
                className="subCategory-filterbar"
                stackProps={{
                  sx: scrollPane,
                }}
              >
                <CategoryFilterBar
                  sx={subCategoryFilterBar}
                  activeCategory={subCategoryId}
                  categories={subCategoryWithActiveParentCategory}
                  onChange={handleChangeSubCategory}
                  renderFilterButton={CategoryFilterChip}
                />
              </HorizontalScrollPane>
            )}
            {!isMultiVenueProject && isMultiOrdinalVenue && (
              <HorizontalScrollPane
                className="level-filterbar"
                stackProps={{
                  sx: scrollPane,
                }}
              >
                <LevelFilterBar
                  sx={levelFilterBar}
                  levels={filteredLevels}
                  onChange={handleChangeOrdinal}
                  value={ordinalFilter}
                />
              </HorizontalScrollPane>
            )}
            {/* Temporary Hide New Multi building filter */}
            {isMultiVenueProject && (
              <Box sx={buildingWithLevelFilterSection}>
                <FormControl sx={buildingFilterSelect}>
                  <InputLabel>{t("Venue")}</InputLabel>
                  <Select
                    onChange={handleChangeVenue}
                    label="venue"
                    value={venueFilter}
                  >
                    {venueOptions.map(({ id, name, value }) => (
                      <MenuItem
                        value={value}
                        sx={selectMenuItem}
                        key={`venue-filter-${id}`}
                      >
                        {t(name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isMultiOrdinalVenue && (
                  <FormControl sx={levelFilterSelect}>
                    <InputLabel>{t("Floor")}</InputLabel>
                    <Select
                      label={"Floor"}
                      value={ordinalFilter}
                      onChange={handleSelectLevelChange}
                      disabled={disableFloorSelect}
                    >
                      {levelOptions.map(({ value, id, name }) => (
                        <MenuItem
                          value={value}
                          key={`select-floor-${id}`}
                          sx={selectMenuItem}
                        >
                          {t(name)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button variant="text" onClick={resetFilter} sx={resetButton}>
                  {t("Clear")}
                </Button>
              </Box>
            )}
            <Divider sx={divider} />
          </Box>

          {hasData ? (
            <List sx={list}>
              {filteredOccupantsSorted.pages.map((page, pageNum) => {
                return (
                  <Fragment key={pageNum}>
                    {page.data.map((occupant) => (
                      // TODO:: Render collapse list item when hasMultipleLocations
                      <ShopListItem
                        key={`${
                          occupant?.properties.isAd ? "ad-" : "directory-item"
                        }${occupant.id}`}
                        occupant={occupant}
                        hideLevel={!isMultiOrdinalVenue}
                      />
                    ))}
                  </Fragment>
                )
              })}
              {hasNextPage &&
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <FeatureSkeletonListItem
                    ref={index === SKELETON_INDEX_TO_REF ? endScrollRef : null}
                    key={index}
                    divider
                  />
                ))}
            </List>
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}

          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
